import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';

const REAUTH_ERROR_CODES = [
  'invalid_grant',
  'login_required',
  'missing_refresh_token',
  'consent_required',
];

const isSessionExpiredError = (error) => {
  const msg = String(error?.message || '');
  const code = String(error?.error || error?.code || '');
  return (
    REAUTH_ERROR_CODES.includes(code) ||
    msg.includes('Missing Refresh Token') ||
    msg.includes('Unknown or invalid refresh token') ||
    msg.includes('invalid_grant') ||
    msg.includes('missing in DB')
  );
};

// Create the context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    getAccessTokenSilently: getAccessTokenFromAuth0,
    loginWithRedirect,
    logout,
    isLoading,
  } = useAuth0();
  const [organizationId, setOrganizationId] = useState(null)
  const [userRole, setUserRole] = useState(null); // Organization role
  const [gamespaceRole, setGamespaceRole] = useState(null); // GameSpace role.
  const[userName, setUserName] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [organizationName, setOrganizationName] = useState('Loading...');
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState(null);
  const [isOrganizationOwner, setIsOrganizationOwner] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const tokenPromiseRef = useRef(null);
  const sessionExpiredRef = useRef(false);
  const organizationIdRef = useRef(null);

  const markSessionExpired = useCallback(() => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    setSessionExpired(true);
  }, []);

  // Coalesce parallel token requests. After an unrecoverable refresh failure,
  // further calls must not exchange the same refresh token again.
  const getAccessTokenSilently = useCallback(async (options = {}) => {
    if (sessionExpiredRef.current) {
      const error = new Error('Reauthentication required');
      error.code = 'reauthentication_required';
      throw error;
    }

    if (!tokenPromiseRef.current) {
      tokenPromiseRef.current = (async () => {
        try {
          return await getAccessTokenFromAuth0({ ...options });
        } catch (error) {
          if (isSessionExpiredError(error)) {
            markSessionExpired();
          }
          throw error;
        } finally {
          tokenPromiseRef.current = null;
        }
      })();
    }
    return await tokenPromiseRef.current;
  }, [getAccessTokenFromAuth0, markSessionExpired]);

  const reauthenticate = useCallback(async () => {
    const returnTo = `${window.location.pathname}${window.location.search}` || '/game-cards';
    const orgId = organizationIdRef.current;

    try {
      await logout({ openUrl: false });
    } catch (error) {
      console.error('Failed to clear Auth0 session cache', error);
    }

    await loginWithRedirect({
      appState: { returnTo },
      authorizationParams: {
        ...(orgId ? { organization: orgId } : {}),
        audience: process.env.REACT_APP_AUDIENCE,
        scope: 'openid profile email offline_access',
      },
    });
  }, [logout, loginWithRedirect]);

  // Organization name is now provided by session data, no need to fetch separately
  const fetchAndUpdateToken = async (options = {}, spaceId) => {
      if (sessionExpiredRef.current) {
        return null;
      }

      if (isAuthenticated && !isLoading) {
        try {
          const accessToken = await getAccessTokenSilently({ ...options });
          
          try {
            const decodedToken = jwtDecode(accessToken);
            console.log('Decoded token:', decodedToken);
            
            const orgId = decodedToken['org_id'] || null;
            organizationIdRef.current = orgId;
            setOrganizationId(orgId);
            
          }
          catch (error) {
            console.error('Error decoding token:', error);
            organizationIdRef.current = null;
            setOrganizationId(null);
          }
          

          // ✅ Fetch user role from backend by Call to backend API directly using fetch to avoid circular dependency useApi
          const spaceQuery = spaceId ? `?space_id=${spaceId}` : '';
          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-role${spaceQuery}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          if (!response.ok) {
            console.warn('User role fetch failed with status:', response.status);
            // Just log and exit — let user stay logged in
            return accessToken;
          }
          
          const data = await response.json();
          setUserRole(data.org_role_id || null);
          // Backend only returns gamespace_role_id when ?space_id= is sent (see /auth/user-role).
          // Without space_id the API always returns null for gamespace_role_id; applying it here
          // races refreshGamespaceRole() and briefly sets gamespaceRole to null (breaking editor UI).
          if (spaceId != null) {
            setGamespaceRole(data.gamespace_role_id || null);
          }
          setUserName(data.username || null);
          setCurrentUserId(data.user_id || null);
          setOrganizationName(data.organization_name || 'Guest User');
          setOrganizationLogoUrl(data.organization_logo_url || null);
          setIsOrganizationOwner(Boolean(data.is_organization_owner));
          
          return accessToken;
          


        } catch (error) {
          console.error(' Error getting access token:', error);
          // Do not fall back to Guest User while Auth0 still reports authenticated.
          return null;
        }
      } else {
        organizationIdRef.current = null;
        setOrganizationId(null);
        setUserRole(null);
        setGamespaceRole(null);
        setUserName(null);
        setCurrentUserId(null);
        setOrganizationName('Guest User');
        setOrganizationLogoUrl(null);
        setIsOrganizationOwner(false);
        return null;
      }
    };
  useEffect(() => {
    // Run once Auth0 finishes loading. Handles both authenticated and guest modes.
    if (!isLoading) {
      fetchAndUpdateToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);

  const refreshUserContext = (spaceId) => fetchAndUpdateToken({ cacheMode: 'off' }, spaceId);

  const refreshGamespaceRole = async (spaceId) => {
    if (sessionExpiredRef.current) {
      return;
    }
    if (!isAuthenticated || !spaceId) {
      setGamespaceRole(null);
      return;
    }
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-role?space_id=${spaceId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setGamespaceRole(data.gamespace_role_id || null);
        console.log('Gamespace role refreshed in AuthContext to role:', data.gamespace_role_id, 'for space:', spaceId);
      } else {
        setGamespaceRole(null);
      }
    } catch (error) {
      console.error('Error refreshing gamespace role:', error);
      setGamespaceRole(null);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, organizationId, userRole, gamespaceRole, loginWithRedirect, logout, isLoading, getAccessTokenSilently, userName, currentUserId, organizationName, organizationLogoUrl, isOrganizationOwner, refreshUserContext, refreshGamespaceRole, sessionExpired, reauthenticate }}>
      {children}
    </AuthContext.Provider>
  );
};
